/**
 * Runtime configuration, resolved once.
 *
 * In dev everything is same-origin: the Vite proxy forwards `/api` to the Hono
 * app's ROOT and `/api/auth` to better-auth, so the session cookie is
 * first-party and no CORS preflight is involved. In a deployed build the SPA is
 * on `app.` and the API on `api.`, which is same-SITE but cross-ORIGIN, so
 * `VITE_API_URL` is set and the cookie adapter's `credentials: 'include'` (plus
 * the API's exact-origin CORS allowlist) is what carries the session.
 */

function origin(): string {
  return typeof window === 'undefined' ? 'http://localhost:5173' : window.location.origin;
}

function absolute(value: string | undefined, fallbackPath: string): string {
  const trimmed = (value ?? '').trim();
  if (trimmed !== '') {
    return trimmed.replace(/\/+$/, '');
  }
  return `${origin()}${fallbackPath}`;
}

/** Base URL for `@crm/api-client`. Must be absolute — oRPC builds `new URL()`. */
export const API_BASE_URL: string = absolute(import.meta.env.VITE_API_URL, '/api');

/** better-auth's mount point. Never rewritten by the proxy (see vite.config.ts). */
export const AUTH_BASE_URL: string = absolute(import.meta.env.VITE_AUTH_URL, '/api/auth');

/** `GET /events/stream` — the SSE channel. */
export const EVENTS_STREAM_URL: string = `${absolute(
  import.meta.env.VITE_EVENTS_URL,
  '/events',
)}/stream`;

/** Stamped into `X-Client-Version` and the error boundary's diagnostics. */
export const CLIENT_VERSION: string = (import.meta.env.VITE_CLIENT_VERSION ?? 'dev').trim();

/**
 * The Chrome extension's id, for `chrome.runtime.sendMessage(EXT_ID, …)`
 * (design §2.11).
 *
 * `null` when unset, which is a first-class state: the bridge reports "no
 * extension id is configured" rather than messaging the empty string. An
 * unlisted CWS build has a stable id, so this is a deploy-time constant rather
 * than something discoverable at runtime — there is no API for a page to ask
 * "which extensions are installed", by design.
 */
export const EXTENSION_ID: string | null =
  (import.meta.env.VITE_EXTENSION_ID ?? '').trim() === ''
    ? null
    : (import.meta.env.VITE_EXTENSION_ID as string).trim();
